import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Star, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import VideoSection from '../components/zimbra-admin-panel/VideoSection';
import CompanyDescription from '../components/company/CompanyDescription';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import AICapabilities from '../components/ai/AICapabilities';
import TrustSection from '../components/trust/TrustSection';
import FaqSection from '../components/trust/FaqSection';
import ProductTile from '../components/products/ProductTile';
import UniversitySection from '../components/university/UniversitySection';
import { products, deploymentModels, type ProductMeta } from '../data/productRegistry';
import { useLocalizedProducts, useLocalizedDeployments } from '../hooks/useLocalizedProducts';

const SectionHeading = ({ badge, title, subtitle, icon: Icon, dark }: {
  badge: string; title: string; subtitle: string; icon: React.ComponentType<{ className?: string }>; dark?: boolean;
}) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 border ${
      dark ? 'border-cyan-400/25 bg-cyan-400/[0.06] text-cyan-200' : 'border-blue-200 bg-blue-50 text-blue-700'
    }`}>
      <Icon className="h-3.5 w-3.5" />
      <span className="text-sm font-semibold tracking-wide">{badge}</span>
    </div>
    <h2 className={`font-display text-4xl lg:text-5xl font-bold mb-4 ${dark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
    <p className={`text-lg max-w-2xl mx-auto ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{subtitle}</p>
  </motion.div>
);

const HomePage = () => {
  const { t } = useTranslation('editor');
  const localizedProducts = useLocalizedProducts();
  const localizedDeployments = useLocalizedDeployments();

  const flagship = products.filter((p) => p.badge === 'Flagship' || p.badge === 'Open Source');
  const fresh = products.filter((p) => p.badge === 'NEW');

  const tile = (p: ProductMeta) => {
    const loc = localizedProducts.find((lp) => lp.slug === p.slug);
    return (
      <ProductTile
        product={p}
        name={loc?.name ?? p.name}
        tagline={loc?.tagline ?? p.tagline}
        description={loc?.description ?? p.description}
        cta={t('productsPage.explore')}
      />
    );
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>
      <div className="pt-[136px]">
        <div id="home">
          <Hero />
        </div>
      </div>

      <section className="relative py-24 bg-slate-950 overflow-hidden">
        <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading dark icon={Star} badge={t('home.flagshipBadge')} title={t('home.flagshipTitle')} subtitle={t('home.flagshipSubtitle')} />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {flagship.map((p, i) => (
              <motion.div key={p.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                {tile(p)}
              </motion.div>
            ))}
          </div>

          {fresh.length > 0 && (
            <div className="mt-24">
              <SectionHeading dark icon={Zap} badge="NEW" title={t('home.newTitle')} subtitle={t('home.newSubtitle')} />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {fresh.map((p, i) => (
                  <motion.div key={p.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}>
                    {tile(p)}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center mt-14">
            <Link
              to="/products"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-cyan-400/40 transition-all"
            >
              {t('home.viewAll')}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <div className="dark">
        <VideoSection />
      </div>

      <UniversitySection />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading icon={Check} badge={t('home.deploymentBadge')} title={t('home.deploymentTitle')} subtitle={t('home.deploymentSubtitle')} />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {deploymentModels.map((dep, i) => {
              const DepIcon = dep.icon;
              const locDep = localizedDeployments.find((d) => d.id === dep.id);
              const features = Array.isArray(locDep?.features) ? locDep!.features : dep.features;
              return (
                <motion.div
                  key={dep.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                >
                <Link
                  to={`/solutions#${dep.id}`}
                  className="group relative flex flex-col h-full rounded-3xl p-7 bg-slate-50 border border-slate-200/70 hover:bg-white hover:border-transparent hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.35)] hover:-translate-y-1 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                >
                  <div className={`absolute inset-x-7 top-0 h-px bg-gradient-to-r ${dep.accent} opacity-0 group-hover:opacity-100 transition-opacity`} />
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${dep.accent} flex items-center justify-center mb-5 shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3`}>
                    <DepIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{locDep?.name ?? dep.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{locDep?.description ?? dep.description}</p>
                  <ul className="space-y-2 flex-1">
                    {features.slice(0, 3).map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 group-hover:text-cyan-600">
                    {t('home.learnMore')}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/solutions"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-slate-950 text-white font-semibold hover:bg-slate-800 transition-colors"
            >
              {t('home.exploreSolutions')}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <div id="company">
        <CompanyDescription />
      </div>

      <AICapabilities />
      <TrustSection />
      <FaqSection />

      <section className="relative py-28 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-60" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[360px] rounded-full bg-gradient-to-r from-blue-600/30 via-cyan-500/30 to-emerald-500/20 blur-[100px]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-[2rem] p-px bg-gradient-to-br from-cyan-300/50 via-white/10 to-emerald-300/40"
          >
            <div className="rounded-[2rem] bg-slate-950/80 backdrop-blur-xl px-8 py-14 sm:px-14 text-center">
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-5">{t('home.pricingTitle')}</h2>
              <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">{t('home.pricingSubtitle')}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/pricing"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-slate-950 bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300 shadow-[0_0_40px_-8px_rgba(34,211,238,0.8)] hover:shadow-[0_0_56px_-6px_rgba(34,211,238,1)] transition-shadow"
                >
                  {t('home.seePricing')}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-white border border-white/20 bg-white/[0.04] hover:bg-white/[0.1] transition-colors"
                >
                  {t('home.requestDemo')}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
};

export default HomePage;
